import Papa from 'papaparse'
import type { DropzoneInputProps } from 'react-dropzone'
import { useDropzone } from 'react-dropzone'
import { useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { useError } from '../useError'

const csvParse = (file: File): any => {
  return new Promise(function (complete, error) {
    Papa.parse(file, { complete, error, skipEmptyLines: true, header: false })
  })
}

export type UseCsvUploadResult<T> = {
  getInputProps: () => DropzoneInputProps
  loading: boolean
  acceptedFile: File
  uploadResult: T
  fileContent: string
  parsedContent: any[][]
  error: string
}

export type UseCsvUploadProps<T> = {
  onUpload: (data: string[][], rawContent: string) => Promise<T>
}

export const useCsvUpload = <T = any>({ onUpload }: UseCsvUploadProps<T>): UseCsvUploadResult<T> => {
  const { onError, error } = useError()
  const [loading, setLoading] = useState(false)
  const [acceptedFile, setAcceptedFile] = useState<File>(null)
  const [uploadResult, setUploadResult] = useState<T>(null)
  const [fileContent, setFileContent] = useState<string>('')
  const [parsedContent, setParsedContent] = useState<any[][]>(null)

  const { getInputProps } = useDropzone({
    multiple: false,
    onDropRejected: (e) => {
      onError(e)
    },
    onDropAccepted: (acceptedFiles) => {
      (async () => {
        try {
          setLoading(true)
          const firstAcceptedFile = acceptedFiles?.[0]
          setAcceptedFile(firstAcceptedFile)
          const type = firstAcceptedFile?.type //text/csv, application/vnd.ms-excel

          let dataContent = []
          if (type === 'text/csv') {
            const parseFile = await csvParse(firstAcceptedFile)
            const errors = parseFile?.errors
            if (!isEmpty(errors)) {
              throw errors
            }
            dataContent = parseFile?.data
          } else {
            throw new Error('Unsupported file type. Please upload a CSV file.')
          }

          const rawContent = await firstAcceptedFile.text()
          setFileContent(rawContent)
          setParsedContent(dataContent)

          //upload it here
          const result = await onUpload(dataContent, rawContent)
          setUploadResult(result)
        } catch (e) {
          onError(e)
        } finally {
          setLoading(false)
        }
      })()
    },
  })

  return {
    loading,
    acceptedFile,
    getInputProps,
    uploadResult,
    fileContent,
    parsedContent,
    error,
  }
}
